import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '..';
import { Icon } from '@iconify/react';

const DashBoardLayerTwo = () => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const locationId = localStorage.getItem('locationID');

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatDate = (d) => d.toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDay));
  const [toDate, setToDate] = useState(formatDate(today));
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState({
    totalQuotations: 0,
    totalQuotationValue: 0,
    approvedQuotations: 0,
    approvedValue: 0,
    pendingQuotations: 0,
    pendingValue: 0,
    rejectedQuotations: 0,
    rejectedValue: 0,
    conversionRate: 0,
    recentQuotations: [],
    topClients: [],
    totalClients: 0,
  });

  const formatCurrency = (num) =>
    Number(num || 0).toLocaleString('en-PK', { minimumFractionDigits: 0 });

  const fetchQuotationsDashboard = async () => {
    try {
      if (!fromDate || !toDate) return;
      setLoading(true);
      let url = `${API_URL}/api/user/dashboard/quotations?startDate=${fromDate}&endDate=${toDate}&locationId=${locationId}`;
      const isAdmin = currentUser?.roles?.role === 'admin' || currentUser?.role === 'admin';
      if (!isAdmin && currentUser?.id) {
        url += `&userId=${currentUser.crm_id}&userRole=user`;
      }
      const response = await fetch(url);
      const json = await response.json();
      if (json.success) setSummary(json.summary);
    } catch (error) {
      console.error('Dashboard fetch failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotationsDashboard();
  }, []);

  const getStatusBadge = (status) => {
    const map = {
      approved: { bg: '#dcfce7', color: '#15803d', label: 'Approved' },
      pending: { bg: '#fef9c3', color: '#a16207', label: 'Pending' },
      rejected: { bg: '#fee2e2', color: '#b91c1c', label: 'Rejected' },
      draft: { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
      unpaid: { bg: '#dbeafe', color: '#1d4ed8', label: 'Active' },
      paid: { bg: '#dcfce7', color: '#15803d', label: 'Converted' },
    };
    const s = (status || '').toLowerCase();
    const found = map[s] || { bg: '#dbeafe', color: '#1d4ed8', label: 'Active' };
    return (
      <span style={{
        background: found.bg,
        color: found.color,
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.3px',
      }}>
        {found.label}
      </span>
    );
  };

  const handleOpenQuotation = (quotation) => {
    const quoteId = quotation?.id || quotation?.quotationId || quotation?.invoice_id || quotation?.invoiceId;
    if (!quoteId) return;
    navigate(`/quote-preview/${quoteId}`);
  };

  const avgValue = summary.totalQuotations
    ? summary.totalQuotationValue / summary.totalQuotations
    : 0;

  const approvalRate = summary.totalQuotations
    ? Math.round((summary.approvedQuotations / summary.totalQuotations) * 100)
    : 0;

  const kpiCards = [
    {
      label: 'Total Quotations',
      value: summary.totalQuotations,
      sub: `Rs ${formatCurrency(summary.totalQuotationValue)} total value`,
      icon: 'mdi:file-document-multiple-outline',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      light: '#ede9fe',
    },
    {
      label: 'Avg. Quote Value',
      value: `Rs ${formatCurrency(avgValue)}`,
      sub: `${summary.conversionRate || 0}% conversion rate`,
      icon: 'mdi:trending-up',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
      light: '#e0f2fe',
    },
    {
      label: 'Unique Clients',
      value: summary.totalClients || 0,
      sub: `${summary.approvedQuotations || 0} quotes approved`,
      icon: 'mdi:account-group-outline',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
      light: '#fef3c7',
    },
    {
      label: 'Approval Rate',
      value: `${approvalRate}%`,
      sub: `${summary.pendingQuotations || 0} still pending`,
      icon: 'mdi:check-circle-outline',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      light: '#d1fae5',
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Filters */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
          <Icon icon="mdi:filter-outline" width="16" />
          Filter by Date
        </div>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={inputStyle}
        />
        <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={inputStyle}
        />
        <button
          onClick={fetchQuotationsDashboard}
          disabled={loading}
          style={{
            background: loading ? '#94a3b8' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 20px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'opacity 0.2s',
          }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm" /> Loading...
            </>
          ) : (
            <>
              <Icon icon="mdi:magnify" width="15" /> Apply
            </>
          )}
        </button>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          onClick={() => navigate('/create-quote')}
          style={{ ...quickButtonStyle, background: 'linear-gradient(135deg, #111827, #374151)' }}
        >
          <Icon icon="mdi:file-document-plus-outline" width="17" />
          Add Quote
        </button>
        <button
          type="button"
          onClick={() => navigate('/create-invoice')}
          style={{ ...quickButtonStyle, background: 'linear-gradient(135deg, #0f766e, #14b8a6)' }}
        >
          <Icon icon="mdi:receipt-text-plus-outline" width="17" />
          Add Order Form
        </button>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        {kpiCards.map((card, i) => (
          <div className="col-xl-3 col-sm-6" key={i}>
            <div style={{
              background: '#fff',
              borderRadius: '14px',
              padding: '20px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              border: '1px solid #f1f5f9',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)';
              }}
            >
              <div style={{
                position: 'absolute', top: -20, right: -20,
                width: 80, height: 80, borderRadius: '50%',
                background: card.light, opacity: 0.7,
              }} />
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: card.gradient, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                marginBottom: '14px',
              }}>
                <Icon icon={card.icon} width="22" color="#fff" />
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                {card.label}
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#1e293b', lineHeight: 1.1, marginBottom: '6px' }}>
                {card.value}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {card.sub}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tables Row */}
      <div className="row g-3">

        {/* Recent Quotations */}
        <div className="col-lg-8">
          <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon="mdi:file-document-outline" width="17" color="#6366f1" />
                </div>
                <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>Recent Quotations</span>
              </div>
              <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                Last 10
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Quote #', 'Client', 'Date', 'Amount', 'Status'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 16px', textAlign: i === 3 ? 'right' : 'left', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!summary.recentQuotations?.length ? (
                    <tr>
                      <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        <Icon icon="mdi:file-search-outline" width="32" style={{ display: 'block', margin: '0 auto 8px' }} />
                        No quotations found for this period
                      </td>
                    </tr>
                  ) : (
                    summary.recentQuotations.map((q, i) => (
                      <tr
                        key={q.id || q.quotationId || i}
                        onClick={() => handleOpenQuotation(q)}
                        title="Open quote preview"
                        style={{
                          borderBottom: '1px solid #f8fafc',
                          cursor: 'pointer',
                          transition: 'background 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = '#fafbff';
                          e.currentTarget.style.transform = 'translateX(2px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#6366f1' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {q.quotationNumber || q.id}
                            <Icon icon="mdi:open-in-new" width="13" color="#94a3b8" />
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#334155', fontWeight: 500 }}>
                          {q.clientName}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>
                          {q.date}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                          Rs {formatCurrency(q.amount)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {getStatusBadge(q.status)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top Clients */}
        <div className="col-lg-4">
          <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', height: '100%' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon icon="mdi:trophy-outline" width="17" color="#f59e0b" />
              </div>
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>Top Clients</span>
            </div>
            {!summary.topClients?.length ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                <Icon icon="mdi:account-off-outline" width="32" style={{ display: 'block', margin: '0 auto 8px' }} />
                No client data available
              </div>
            ) : (
              summary.topClients.map((client, i) => {
                const colors = ['#6366f1', '#0ea5e9', '#f59e0b', '#10b981', '#f43f5e'];
                const color = colors[i % colors.length];
                const initials = (client.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={i} style={{
                    padding: '14px 20px',
                    borderBottom: i < summary.topClients.length - 1 ? '1px solid #f8fafc' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'background 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: '38px', height: '38px', borderRadius: '10px',
                      background: color + '1a', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: 800, color, flexShrink: 0,
                    }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {client.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        {client.quotationCount} quote{client.quotationCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      Rs {formatCurrency(client.totalValue)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

const inputStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '7px 12px',
  fontSize: '13px',
  color: '#334155',
  outline: 'none',
  background: '#f8fafc',
};

const quickButtonStyle = {
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  boxShadow: '0 6px 14px rgba(15, 23, 42, 0.12)',
  whiteSpace: 'nowrap',
};

export default DashBoardLayerTwo;
